import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  MissingField,
  MissingFieldsSummary,
} from "src/components/common/MissingFieldsSummary";
import { UsageIndicator } from "src/components/common/UsageIndicator";
import { usePublicTrial } from "src/contexts/PublicTrialContext";
import { useFunnyMessages } from "src/hooks/useFunnyMessages";
import { useUsage } from "src/hooks/useUsage";
import { generateCorrespondence } from "src/services/correspondence.service";
import { formatDisplayText } from "src/utils/markdownUtils";
import { getLogger } from "src/utils/logger";
import { CopyButton } from "../../common/CopyButton";

type CorrespondenceType = "newsletter" | "email" | "blogpost";

interface CorrespondenceFormProps {
  onBack: () => void;
}

interface CorrespondenceFormData {
  type: CorrespondenceType;
  newsletterAudience: string;
  newsletterTitle: string;
  newsletterInformation: string;
  emailType: string;
  emailRecipient: string;
  emailContext: string;
  postAudience: string;
  postTitle: string;
  postInformation: string;
}

interface Option<TValue extends string | number = string> {
  value: TValue;
  label: string;
}

const LOG = getLogger("CorrespondenceForm");

const correspondenceTypes: Option<CorrespondenceType>[] = [
  { value: "newsletter", label: "Newsletter" },
  { value: "email", label: "Email" },
  { value: "blogpost", label: "Post" },
];

const newsletterAudiences: Option[] = [
  { value: "parents", label: "Parents & Guardians" },
  { value: "students", label: "Students" },
  { value: "staff", label: "Staff" },
  { value: "community", label: "School Community" },
];

const emailTypes: Option[] = [
  { value: "parent-communication", label: "Parent Communication" },
  { value: "staff", label: "Staff" },
  { value: "external-professional", label: "External Professional" },
];

const postAudiences: Option[] = [
  { value: "community", label: "School Community" },
  { value: "parents", label: "Parents & Guardians" },
  { value: "students", label: "Students" },
  { value: "staff", label: "Staff" },
];

const initialFormData: CorrespondenceFormData = {
  type: "newsletter",
  newsletterAudience: "",
  newsletterTitle: "",
  newsletterInformation: "",
  emailType: "parent-communication",
  emailRecipient: "",
  emailContext: "",
  postAudience: "",
  postTitle: "",
  postInformation: "",
};

export const CorrespondenceForm: React.FC<CorrespondenceFormProps> = ({
  onBack,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isPublicTrial, requestTrialSignup } = usePublicTrial();
  const {
    getUsageForProduct,
    loading: usageLoading,
    refresh: refreshUsage,
  } = useUsage();
  const usage = getUsageForProduct("correspondence");
  const canUse = isPublicTrial ? true : usage?.can_use ?? false;

  const [formData, setFormData] =
    useState<CorrespondenceFormData>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const generatedContentRef = useRef<HTMLDivElement | null>(null);
  const funnyMessage = useFunnyMessages(isLoading);

  const currentLabel =
    correspondenceTypes.find((option) => option.value === formData.type)
      ?.label ?? "Correspondence";

  useEffect(() => {
    if (!generatedContent) return;

    generatedContentRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [generatedContent]);

  const updateField = <TKey extends keyof CorrespondenceFormData>(
    name: TKey,
    value: CorrespondenceFormData[TKey]
  ) => {
    setGeneratedContent("");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    updateField(
      name as keyof CorrespondenceFormData,
      value as CorrespondenceFormData[keyof CorrespondenceFormData]
    );
  };

  const getMissingFields = (): MissingField[] => {
    const missing: MissingField[] = [];

    if (formData.type === "newsletter") {
      if (!formData.newsletterAudience) {
        missing.push({
          label: "Choose a primary audience",
          anchorId: "correspondence-section-newsletter-audience",
        });
      }
      if (!formData.newsletterInformation.trim()) {
        missing.push({
          label: "Add the newsletter information or details",
          anchorId: "newsletterInformation",
        });
      }
    }

    if (formData.type === "email" && !formData.emailContext.trim()) {
      missing.push({
        label: "Add the email content or context",
        anchorId: "correspondence-section-email-context",
      });
    }

    if (formData.type === "blogpost") {
      if (!formData.postAudience) {
        missing.push({
          label: "Choose a primary audience",
          anchorId: "correspondence-section-post-audience",
        });
      }
      if (!formData.postInformation.trim()) {
        missing.push({
          label: "Add the post information or details",
          anchorId: "postInformation",
        });
      }
    }

    return missing;
  };

  const missingFields = getMissingFields();
  const isFormValid = missingFields.length === 0;

  const getPayloadData = () => {
    if (formData.type === "newsletter") {
      return {
        audience: formData.newsletterAudience,
        title: formData.newsletterTitle,
        information: formData.newsletterInformation,
      };
    }

    if (formData.type === "email") {
      return {
        emailType: formData.emailType,
        recipient: formData.emailRecipient,
        context: formData.emailContext,
      };
    }

    return {
      platform: "school website or social media",
      contentType: "General information",
      tone: "informative",
      targetAudience: formData.postAudience,
      wordLimit: 100,
      content: [formData.postTitle, formData.postInformation]
        .filter(Boolean)
        .join("\n\n"),
    };
  };

  const getGeneratedTitle = () => {
    if (formData.type === "newsletter") return "Generated Newsletter Content";
    if (formData.type === "email") return "Generated Email Content";
    return "Generated Post Content";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsLoading(true);
    setError(null);

    try {
      if (isPublicTrial) {
        requestTrialSignup();
        setIsLoading(false);
        return;
      }

      const accessToken = await getAccessTokenSilently();
      const response = await generateCorrespondence(
        {
          type: formData.type,
          data: getPayloadData(),
        },
        accessToken
      );

      if (response.error || !response.data) {
        throw new Error(
          response.error?.message || "Failed to generate correspondence content"
        );
      }

      setGeneratedContent(response.data.content);
      refreshUsage();
    } catch (err) {
      LOG.error("Error generating correspondence:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to generate correspondence content"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderRadioGroup = <TValue extends string | number>(
    name: string,
    selectedValue: TValue,
    options: Option<TValue>[],
    onSelect: (value: TValue) => void,
    className = ""
  ) => (
    <div className={`radio-group ${className}`}>
      {options.map((option) => (
        <div
          key={option.value}
          className={`radio-item ${
            selectedValue === option.value ? "selected" : ""
          }`}
          onClick={() => onSelect(option.value)}
        >
          <input
            type="radio"
            id={`${name}-${option.value}`}
            name={name}
            value={option.value}
            checked={selectedValue === option.value}
            onChange={() => onSelect(option.value)}
          />
          <div className="radio-custom"></div>
          <label htmlFor={`${name}-${option.value}`}>{option.label}</label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="form-container correspondence-form">
      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-group correspondence-type-selector">
            <label>What would you like to write? *</label>
            {renderRadioGroup(
              "correspondenceType",
              formData.type,
              correspondenceTypes,
              (value) => updateField("type", value)
            )}
          </div>

          {formData.type === "newsletter" && (
            <>
              <div
                className="form-group"
                id="correspondence-section-newsletter-audience"
              >
                <label>Primary Audience *</label>
                {renderRadioGroup(
                  "newsletterAudience",
                  formData.newsletterAudience,
                  newsletterAudiences,
                  (value) => updateField("newsletterAudience", value)
                )}
              </div>

              <div className="form-group">
                <label htmlFor="newsletterTitle">
                  Newsletter Title (Optional)
                </label>
                <input
                  type="text"
                  id="newsletterTitle"
                  name="newsletterTitle"
                  value={formData.newsletterTitle}
                  onChange={handleInputChange}
                  placeholder="e.g., Monthly Update, Term 2 News, Special Events"
                />
              </div>

              <div className="form-group">
                <label htmlFor="newsletterInformation">Information</label>
                <textarea
                  id="newsletterInformation"
                  name="newsletterInformation"
                  value={formData.newsletterInformation}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Include any specific information, dates, or details you'd like incorporated into the newsletter..."
                />
              </div>
            </>
          )}

          {formData.type === "email" && (
            <>
              <div className="form-group">
                <label>Email Type *</label>
                {renderRadioGroup(
                  "emailType",
                  formData.emailType,
                  emailTypes,
                  (value) => updateField("emailType", value)
                )}
              </div>

              <div className="form-group">
                <label htmlFor="emailRecipient">Recipient (Optional)</label>
                <input
                  type="text"
                  id="emailRecipient"
                  name="emailRecipient"
                  value={formData.emailRecipient}
                  onChange={handleInputChange}
                  placeholder="e.g., Mr. and Mrs. Smith, Teaching Team, Dr. Johnson"
                />
              </div>

              <div
                className="form-group"
                id="correspondence-section-email-context"
              >
                <label htmlFor="emailContext">Email Content/Context *</label>
                <textarea
                  id="emailContext"
                  name="emailContext"
                  value={formData.emailContext}
                  onChange={handleInputChange}
                  rows={6}
                  placeholder="Provide the main content and context for your email. Include specific details, concerns, achievements, or information you want to communicate..."
                  required
                />
              </div>
            </>
          )}

          {formData.type === "blogpost" && (
            <>
              <div
                className="form-group"
                id="correspondence-section-post-audience"
              >
                <label>Primary Audience *</label>
                {renderRadioGroup(
                  "postAudience",
                  formData.postAudience,
                  postAudiences,
                  (value) => updateField("postAudience", value)
                )}
              </div>

              <div className="form-group">
                <label htmlFor="postTitle">Post Title (Optional)</label>
                <input
                  type="text"
                  id="postTitle"
                  name="postTitle"
                  value={formData.postTitle}
                  onChange={handleInputChange}
                  placeholder="e.g., Sports Carnival Update, Book Week Reminder, Class News"
                />
              </div>

              <div className="form-group">
                <label htmlFor="postInformation">Information</label>
                <textarea
                  id="postInformation"
                  name="postInformation"
                  value={formData.postInformation}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Include any specific information, dates, or details you'd like incorporated into the post..."
                />
              </div>
            </>
          )}
        </div>

        {error && (
          <div className="form-error-message">
            <p>{error}</p>
          </div>
        )}

        <MissingFieldsSummary missingFields={missingFields} />

        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={onBack}>
            Back
          </button>
          <button
            type="submit"
            className="primary-button"
            disabled={isLoading || !isFormValid || !canUse}
          >
            {isLoading ? funnyMessage : `Generate ${currentLabel}`}
          </button>
        </div>
      </form>

      {!generatedContent && (
        <div className="usage-indicators-container">
          {usageLoading ? (
            <UsageIndicator
              productName="Parent/Guardian Correspondence"
              used={0}
              limit={null}
              remaining={null}
              canUse={false}
              period={new Date().toLocaleDateString("en-AU", {
                year: "numeric",
                month: "long",
              })}
              loading={true}
            />
          ) : usage ? (
            <UsageIndicator
              productName={usage.name}
              used={usage.used}
              limit={usage.limit}
              remaining={usage.remaining}
              canUse={usage.can_use}
              period={new Date().toLocaleDateString("en-AU", {
                year: "numeric",
                month: "long",
              })}
              loading={false}
            />
          ) : (
            <div className="usage-loading">
              <p>Loading usage information...</p>
            </div>
          )}
        </div>
      )}

      {generatedContent && (
        <div className="generated-content-section" ref={generatedContentRef}>
          <div className="content-header">
            <h2>{getGeneratedTitle()}</h2>
            <CopyButton content={generatedContent} />
          </div>

          <div className="generated-content">
            <pre>{formatDisplayText(generatedContent)}</pre>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => setGeneratedContent("")}
            >
              Generate Again
            </button>
            <button type="button" className="primary-button" onClick={onBack}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
